<template>
  <div id="container" class="container">
    <div id="title-of-save">
      <h1 id="title-text-child">
        Create Campaign
      </h1>
    </div>
    <div class="row">
      <div class="col-1 margin-bottom-2">
        <DevAppDropDownComponent role="campaign_manager" @devapp-changed="devAppChanged"/>
      </div>
    </div>
    <div class="row">
      <div class="col-1 margin-bottom-2">
        <sdx-input
            id="campainLabel"
            label="Campaign Name"
            class="sdx-input"
            placeholder="Name"
            :value="campaignName"
            @input="(event) => setCampaignName(event)"
        />
      </div>
    </div>
    <div class="row">
      <div class="col-1 margin-bottom-2">
        <form method="post" action="" enctype="multipart/form-data">
          <span class="font--light">CSV or Excel File  </span>
          <sdx-menu-flyout>
            <sdx-menu-flyout-toggle>
              <sdx-icon
                  icon-name="icon-information-circle"
                  sr-hint="Read more"
              />
            </sdx-menu-flyout-toggle>

            <sdx-menu-flyout-content style="width: 600px" class="sdx-menu-img">
              <span>
                Upload the file containing the data for the campaign.<br/>
                It needs to have at least the column for the recipient phone numbers.<br/>
                In addition there can be further columns for dynamic content that can be inserted into the message text in a second step.<br/>
              </span>
              <br/>
              <br/>
              <p class="margin-bottom-0 font--semi-bold">Example files:</p>
              <ul class="list single-line margin-top-1">
                <li>
                  <a href="~@/assets/csv-example1.csv" class="btn btn-primary" download>
                    <i class="icon icon-document"/>
                    Example 1
                  </a>
                  : text elements
                </li>
                <li>
                  <a :href="'/csv-example2.csv'" class="btn btn-primary" download>
                    <i class="icon icon-document"/>
                    Example 2
                  </a>
                  : entire message content
                </li>
              </ul>
              <p class="margin-bottom-0 font--semi-bold">Restrictions:</p>
              <ul class="list single-line margin-top-1">
                <li>Line breaks are not allowed in CSV entries.</li>
                <li>Column entries used in sms text are limited to {{ formatBigNumber(this.placeholderMaxLength) }} characters.</li>
                <li>Maximum file size is {{ this.maxFileSizeMB }} MB.</li>
                <li>Maximum {{ formatBigNumber(this.maxRecipients) }} recipients per Campaign.</li>
                <li>SMS sending is throttled to 1 SMS/s</li>
              </ul>
            </sdx-menu-flyout-content>
          </sdx-menu-flyout>

          <input
              type="file"
              ref="csvFiles"
              id="files"
              @change="checkFile()"
              accept=".csv,.xsls,.xlsx"
              class="col-1 button button--secondary margin-bottom-1"/>
          <div v-if="fileSizeExceeded" class="error">
            <b>!</b> File size exceeded the limit of {{ this.maxFileSizeMB }} MB: {{ this.fileSizeExceededHint }}<br/>
          </div>
          <div v-if="tooManyRecipients" class="error">
            <b>!</b> Too many recipients: {{ this.tooManyRecipientsHint }}
          </div>
        </form>
      </div>
    </div>
    <div class="row flex-items-xs-between">
      <div class="col-xs-4">
        <sdx-button
            label="Overview"
            theme="secondary"
            @click="goToRoute('/campaigns')"
        />
      </div>
      <div class="col-xs-4 text-align-right">
        <sdx-button
            ref="buttonNext"
            name="buttonNext"
            label="Next"
            class="sdx-button-object"
            @click="saveCampaignAndGoToEditor()"
            :disabled="!formValid"
            loading="false"
        />
      </div>
    </div>
  </div>
</template>

<script>

import DevAppDropDownComponent from "@/components/DevAppDropDownComponent.vue";
import campaignsService from "@/modules/campaigns/service";
import {logger} from "@/services/logger.service";
import {showNotification, showSuccess} from "@/services/notification.service";
import {computed} from "vue";
import {formatBigNumber} from "@/util/utility";


export default {
  components: {
    DevAppDropDownComponent,
  },
  data() {
    return {
      formValid: false,
      campaignId: null,
      campaignName: '',
      webId: undefined,
      maxFileSizeMB: 2,
      maxFileSize: computed(() => this.maxFileSizeMB * 1024 * 1024), //2MB,
      maxRecipients: 25030,
      placeholderMaxLength: 600,
      fileSizeExceeded: false,
      fileSizeExceededHint: '',
      tooManyRecipients: false,
      tooManyRecipientsHint: '',
    };
  },
  async mounted() {
    //Load Config
    const response = await campaignsService.getCampaignsConfig();
    const campaignsConfig = response.data;
    this.maxFileSizeMB = campaignsConfig.springMultiPartMaxFileSizeMB;
    this.maxRecipients = campaignsConfig.maxRecipients
    this.placeholderMaxLength = campaignsConfig.placeholderMaxLength
    logger.debug("campaignsConfig:" + JSON.stringify(campaignsConfig))
    //Load Campaing if defined
    if (this.$route.query.campaignId) {
      this.campaignId = this.$route.query.campaignId;
      logger.debug("Load campaign: " + this.campaignId)
      let response = await campaignsService.getCampaign(this.webId, this.campaignId)
      this.campaignName = response.data.name
    }
  },
  methods: {
    formatBigNumber,
    devAppChanged: function (webId) {
      this.webId = webId;
      campaignsService.setCampaignWebId(webId);
      campaignsService.removeCampaignsList();
      this.validateForm()
    },
    setCampaignName(event) {
      this.campaignName = event.srcElement.value;
      this.validateForm();
    },
    async saveCampaignAndGoToEditor() {
      //For the future you could make a util function to check for empty or length or type
      this.$refs.buttonNext.loading=true;
      try {
        if (this.campaignId) {
          //Update Campaign
          try {
            await campaignsService.updateCampaignCsv(this.webId, this.campaignId, this.campaignName, this.$refs.csvFiles.files);
          } catch {
            showNotification("Structure of new CSV generates conflict, please fix it here!")
          }
        } else {
          //Create Campaign
          const response = await campaignsService.createCampaign(this.webId, this.campaignName, this.$refs.csvFiles.files);
          this.campaignId = response.data;
        }
        logger.debug("CampaignId: " + this.campaignId);
        const response2 = await campaignsService.getCampaign(this.webId, this.campaignId);
        campaignsService.setCampaignToSession(response2.data);
        showSuccess('Campaign saved successfully')
        this.$router.push('/campaigns/edit');
      } catch (error) {
        this.campaignId = undefined;
      }
      this.$refs.buttonNext.loading = false;
    },
    async checkRecipients(file) {
      this.tooManyRecipientsHint = '';
      let reader = new FileReader()
      reader.onloadend = (e) => {
        let lines = e.target.result.split("\n").length;
        this.tooManyRecipients = (lines > this.maxRecipients)
        logger.debug(this.tooManyRecipients + "/" + lines)
        if (this.tooManyRecipients) {
          this.tooManyRecipientsHint = "Found " + formatBigNumber(lines) + " lines in file, but only " + formatBigNumber(this.maxRecipients) + " are allowed!"
          logger.debug("tooManyRecipientsHint: " + this.tooManyRecipientsHint)
        }
        this.validateForm()
      }
      await reader.readAsText(file)
    },
    async checkFile() {
      this.checkRecipients(this.$refs.csvFiles.files[0]);
      this.fileSizeExceededHint = '';
      let fileSize = this.$refs.csvFiles.files[0].size;
      this.fileSizeExceeded = (fileSize >= this.maxFileSize)
      if (this.fileSizeExceeded) {
        this.fileSizeExceededHint = "File has " + (fileSize / (1024 * 1024)) + " MB, but only " + (this.maxFileSize / (1024 * 1024)) + " MB are allowed!"
        logger.debug("fileSizeExceededHint: " + this.fileSizeExceededHint)
      }
      this.validateForm()
    },
    goToRoute(route) {
      this.$router.push(route);
    },
    validateForm() {
      this.formValid = this.webId !== undefined && this.campaignName !== null && this.campaignName !== '' && this.$refs.csvFiles.files !== undefined && !this.tooManyRecipients && !this.fileSizeExceeded
      logger.debug("formValid: " + this.formValid)
      logger.debug("tooManyRecipients: " + this.tooManyRecipients)
      logger.debug("fileSizeExceeded: " + this.fileSizeExceeded)
    },
  },

};
</script>

<style>
.error {
  font-size: 16px;
  color: #DD1122;
}
</style>
